import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SpinnerIcon from "../global/SpinnerIcon";
import { fetchApiUsage } from "../../store/actions/apiAccessAction";
import {
  historyDateLabel,
  monthLabel,
  periodLabel,
  resetWhen,
  usageLevel,
} from "../../utils/apiUsage";

/*
    API usage panel (static#9): "B · Fact rows" with the status pill from "A",
    as approved by Dave. It replaced the old 30-day table on the API
    Management page, and loads its own data when it mounts.

    Rows: the current period against its limit, this month's total, and
    previous months behind a button. See utils/apiUsage.js for the rules.
*/

const PILL_TEXT = {
  ok: "On track",
  near: "Near limit",
  full: "Limit reached",
};

const Row = ({ label, children }) => (
  <div className="usage-panel__row">
    <div className="usage-panel__label">{label}</div>
    <div className="usage-panel__value">{children}</div>
  </div>
);

const Header = ({ tone }) => (
  <div className="usage-panel__header">
    <h2 className="usage-panel__title" id="usage-panel-title">
      Usage
    </h2>
    {tone && (
      <span
        className={`usage-panel__pill usage-panel__pill--${tone}`}
        data-testid="usage-pill"
      >
        <span aria-hidden="true">● </span>
        {PILL_TEXT[tone]}
      </span>
    )}
  </div>
);

const PreviousMonths = ({ months, historyStartsAt }) => {
  const [open, setOpen] = useState(false);

  if (months.length === 0) {
    return <span className="usage-panel__muted">No earlier months yet.</span>;
  }

  const count = `${months.length} ${months.length === 1 ? "month" : "months"}`;
  const since = historyStartsAt ? historyDateLabel(historyStartsAt) : null;

  return (
    <>
      <button
        type="button"
        className="usage-panel__toggle"
        aria-expanded={open}
        aria-controls="usage-panel-months"
        onClick={() => setOpen((value) => !value)}
      >
        {open ? "Hide" : "Show"} {count}{" "}
        <span
          className={`usage-panel__chevron${
            open ? " usage-panel__chevron--open" : ""
          }`}
          aria-hidden="true"
        >
          ›
        </span>
      </button>
      {open && (
        <div id="usage-panel-months">
          <table className="usage-panel__months">
            <thead className="sr-only">
              <tr>
                <th scope="col">Month</th>
                <th scope="col">Calls</th>
              </tr>
            </thead>
            <tbody>
              {months.map((row) => (
                <tr key={row.month}>
                  <td>{monthLabel(row.month)}</td>
                  <td>{row.used.toLocaleString("en-US")}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="usage-panel__muted usage-panel__note">
            {since
              ? `History starts ${since}, when your current key was issued. Months are UTC.`
              : "Months are UTC."}
          </div>
        </div>
      )}
    </>
  );
};

const CurrentPeriod = ({ usage }) => {
  const { used, limit, period, resetsAt } = usage;
  const { tone, percent } = usageLevel(used, limit);
  const reset = resetWhen(resetsAt);

  let resetLine = null;
  if (reset && tone === "full") {
    resetLine = (
      <>
        <strong className="usage-panel__limit">Limit reached</strong> · calls
        are paused until {reset.phrase}
      </>
    );
  } else if (reset) {
    resetLine = `Resets ${reset.sameDay ? "at " : ""}${reset.phrase}`;
  }

  return (
    <Row label={periodLabel(period)}>
      <div className="usage-panel__count">
        {used.toLocaleString("en-US")} / {limit.toLocaleString("en-US")}
      </div>
      <div
        className="usage-panel__bar"
        role="progressbar"
        aria-label={periodLabel(period)}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={Math.min(used, limit)}
      >
        <span
          className={`usage-panel__fill usage-panel__fill--${tone}`}
          data-testid="usage-fill"
          style={{ width: `${percent}%` }}
        />
      </div>
      {resetLine && <div className="usage-panel__muted">{resetLine}</div>}
    </Row>
  );
};

const UsagePanel = () => {
  const dispatch = useDispatch();
  const { loading, error, data } = useSelector((state) => state.apiUsage);

  useEffect(() => {
    dispatch(fetchApiUsage());
  }, [dispatch]);

  // Never show numbers alongside a failure — not zeros, not a stale load.
  if (error) {
    return (
      <section className="usage-panel mb-4" aria-labelledby="usage-panel-title">
        <Header />
        <div className="usage-panel__muted">
          Usage is unavailable right now.
        </div>
      </section>
    );
  }

  if (!data) {
    return (
      <section
        className="usage-panel mb-4"
        aria-labelledby="usage-panel-title"
        aria-busy={loading}
      >
        <Header />
        {/*
          The same three rows the loaded panel has, with placeholder bars in
          place of values, so nothing below the panel moves when the numbers
          arrive. The spinner stays for screen readers and as the visible cue.
        */}
        <Row
          label={
            <span className="usage-panel__skeleton usage-panel__skeleton--label" />
          }
        >
          <span
            className="usage-panel__skeleton usage-panel__skeleton--value"
            data-testid="usage-skeleton-row"
          />
          <span
            className="usage-panel__skeleton usage-panel__skeleton--bar"
            data-testid="usage-skeleton-bar"
          />
          <span className="usage-panel__skeleton usage-panel__skeleton--line">
            <SpinnerIcon label="Loading usage" />
          </span>
        </Row>
        <Row
          label={
            <span className="usage-panel__skeleton usage-panel__skeleton--label" />
          }
        >
          <span
            className="usage-panel__skeleton usage-panel__skeleton--value"
            data-testid="usage-skeleton-row"
          />
        </Row>
        <Row
          label={
            <span className="usage-panel__skeleton usage-panel__skeleton--label" />
          }
        >
          <span
            className="usage-panel__skeleton usage-panel__skeleton--value"
            data-testid="usage-skeleton-row"
          />
        </Row>
      </section>
    );
  }

  const unlimited = data.limit === null;
  const tone = unlimited ? null : usageLevel(data.used, data.limit).tone;

  return (
    <section className="usage-panel mb-4" aria-labelledby="usage-panel-title">
      <Header tone={tone} />
      {!unlimited && <CurrentPeriod usage={data} />}
      {/* For a monthly plan the first row already is this month. */}
      {(unlimited || data.period !== "MONTH") && (
        <Row label="This month">
          <span className="usage-panel__num">
            {data.thisMonthUsed.toLocaleString("en-US")} calls
          </span>
          {unlimited && (
            <div className="usage-panel__muted">No call limit on your plan</div>
          )}
        </Row>
      )}
      <Row label="Previous months">
        <PreviousMonths
          months={data.months}
          historyStartsAt={data.historyStartsAt}
        />
      </Row>
    </section>
  );
};

export default UsagePanel;
